import { useTranslation } from 'react-i18next';
import { Globe, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ApiCallError } from '@/lib/api';
import {
  useGrantPatientPortalAccess,
  usePatientPortalInvitation,
  useRevokePatientPortalAccess,
} from '@/hooks/usePatientPortal';
import type { PatientRow } from '@/hooks/usePatients';

interface PatientPortalSectionProps {
  patient: PatientRow;
  canManage: boolean;
  variant?: 'card' | 'inline';
}

export function PatientPortalSection({
  patient,
  canManage,
  variant = 'card',
}: PatientPortalSectionProps) {
  const { t } = useTranslation();
  const grant = useGrantPatientPortalAccess(patient.id);
  const revoke = useRevokePatientPortalAccess(patient.id);
  const { data: pendingInvite, isLoading: inviteLoading } = usePatientPortalInvitation(patient.id);

  const hasPortalAccess = Boolean(patient.portal_user_id);
  const hasEmail = Boolean(patient.email?.trim());

  const onGrant = async () => {
    try {
      const result = await grant.mutateAsync();
      if (result.status === 'linked') {
        toast.success(t('patients.portal.toast_linked', { email: result.email }));
      } else {
        toast.success(t('patients.portal.toast_invited', { email: result.email }));
      }
    } catch (err) {
      if (err instanceof ApiCallError) {
        if (err.code === 'conflict') {
          toast.error(t('patients.portal.error_already_has_access'));
        } else if (err.code === 'invalid-input') {
          toast.error(err.message);
        } else {
          toast.error(t('patients.portal.error_grant_failed'));
        }
      } else {
        toast.error(t('patients.portal.error_grant_failed'));
      }
    }
  };

  const onRevoke = async () => {
    if (!window.confirm(t('patients.portal.confirm_revoke'))) return;

    try {
      await revoke.mutateAsync();
      toast.success(t('patients.portal.toast_revoked'));
    } catch (err) {
      if (err instanceof ApiCallError) {
        toast.error(err.message || t('patients.portal.error_revoke_failed'));
      } else {
        toast.error(t('patients.portal.error_revoke_failed'));
      }
    }
  };

  const statusContent = inviteLoading ? (
    <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
  ) : hasPortalAccess ? (
    <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400">
      <ShieldCheck className="h-3.5 w-3.5" />
      {t('patients.portal.status_active')}
    </span>
  ) : pendingInvite ? (
    <span className="text-amber-700 dark:text-amber-400">{t('patients.portal.status_pending')}</span>
  ) : !hasEmail ? (
    <span className="text-amber-700 dark:text-amber-300">{t('patients.portal.needs_email')}</span>
  ) : (
    <span className="text-gray-500">{t('patients.portal.not_enabled')}</span>
  );

  const actionButton =
    canManage && hasPortalAccess ? (
      <button
        type="button"
        onClick={onRevoke}
        disabled={revoke.isPending}
        className="ml-2 text-xs font-medium text-gray-500 hover:text-red-600 dark:hover:text-red-400"
      >
        {revoke.isPending ? '…' : t('patients.portal.revoke')}
      </button>
    ) : canManage && !hasPortalAccess && hasEmail && !pendingInvite ? (
      <button
        type="button"
        onClick={onGrant}
        disabled={grant.isPending}
        className="ml-2 text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
      >
        {grant.isPending ? t('patients.portal.granting') : t('patients.portal.grant')}
      </button>
    ) : null;

  if (variant === 'inline') {
    return (
      <span className="inline-flex items-center gap-1">
        <Globe className="h-3.5 w-3.5 text-gray-400" />
        <span className="font-medium text-gray-600 dark:text-gray-300">{t('patients.portal.title')}:</span>
        {statusContent}
        {actionButton}
      </span>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-card-dark">
      <div className="mb-3 flex items-center gap-2">
        <Globe className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          {t('patients.portal.title')}
        </h3>
      </div>

      <p className="mb-3 text-xs text-gray-600 dark:text-gray-400">
        {t('patients.portal.description')}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        {statusContent}
        {actionButton}
      </div>
    </div>
  );
}
